import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Plus, ChevronDown, ChevronRight, RotateCw, FlaskConical } from 'lucide-react';
import {
  type SynthesizedRun,
  listSynthesizedRuns,
  materializeRun,
  synthesizeCases,
} from '../api/evalSynthesize';
import { streamJobEvents } from '../api/sse';

const STATUS_META: Record<SynthesizedRun['status'], { label: string; badge: string }> = {
  pending: { label: '排队中', badge: 'info' },
  running: { label: '合成中', badge: 'info' },
  succeeded: { label: '已完成', badge: 'success' },
  failed: { label: '失败', badge: 'error' },
};

const CASE_TYPE_LABEL: Record<string, string> = {
  qa: '黄金问答',
  tool_call: '工具调用',
  boundary: '越界拒绝',
};

interface Props {
  agentId: string;
  /** materialize 成功后回调（携带 suite_id，用于跳转创建目标验证） */
  onMaterialized?: (suiteId: string) => void;
}

/**
 * LLM 模拟用户合成评测用例（B8）：填数量/关注点 → LLM 生成场景 →
 * 扮演用户与被评 Agent 多轮模拟 → 用例列表预览 → 「转为套件并创建目标验证」。
 * 进度经 JobEvent 游标重放（刷新页面不丢进度）；LLM 未配置/失败诚实降级展示。
 */
export default function EvalSynthesizePanel({ agentId, onMaterialized }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [numCases, setNumCases] = useState('3');
  const [focus, setFocus] = useState('');
  const [maxTurns, setMaxTurns] = useState('6');
  const [runs, setRuns] = useState<SynthesizedRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [materializingId, setMaterializingId] = useState<string | null>(null);
  const [materialized, setMaterialized] = useState<{ runId: string; suiteId: string; suiteName: string } | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refreshRuns = useCallback(async () => {
    if (!agentId) return;
    try {
      const data = await listSynthesizedRuns(agentId);
      setRuns(data.runs);
    } catch (e) {
      console.error('加载合成批次失败:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    const tick = () => {
      void refreshRuns();
    };
    tick();
  }, [refreshRuns]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleCreate = async () => {
    if (!agentId) return;
    setError('');
    setProgress('');
    setMaterialized(null);
    const num = Number(numCases);
    const turns = Number(maxTurns);
    if (!Number.isInteger(num) || num < 1 || num > 10) {
      setError('用例数量需为 1~10 的整数');
      return;
    }
    if (!Number.isInteger(turns) || turns < 2 || turns > 12) {
      setError('对话轮数需为 2~12 的整数');
      return;
    }
    setCreating(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const { run_id, job_id } = await synthesizeCases(agentId, {
        num_cases: num,
        focus: focus.trim() || null,
        max_turns: turns,
      });
      setProgress('已创建合成任务，等待执行…');
      await streamJobEvents(
        job_id,
        (event) => {
          if (event.type === 'progress') {
            const stage = (event.data as { stage?: string }).stage;
            if (stage === 'scenarios') setProgress('LLM 正在生成评测场景…');
            else if (stage === 'simulate') {
              const d = event.data as { index?: number; total?: number; title?: string };
              setProgress(`模拟场景 ${d.index}/${d.total}：${d.title || ''}`);
            } else if (stage === 'case_ready') {
              setProgress(`场景模拟完成（${(event.data as { case_id?: string }).case_id || ''}）`);
            }
          }
          if (event.type === 'done') {
            const data = event.data as { status?: string; error?: string; result?: { status?: string } };
            if (data.status === 'failed' || data.result?.status === 'failed') {
              setError(data.error || '合成失败（LLM 调用失败或输出不合契约）');
            }
            setProgress('');
          }
        },
        controller.signal
      );
      setExpanded(true);
      setExpandedRunId(run_id);  // 自动展开新批次，展示用例预览
      await refreshRuns();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || '创建合成任务失败（请确认已配置 LLM）');
    } finally {
      setCreating(false);
      setProgress('');
    }
  };

  const handleMaterialize = async (run: SynthesizedRun) => {
    setMaterializingId(run.id);
    setError('');
    try {
      const result = await materializeRun(run.id, 'skill');
      setMaterialized({ runId: run.id, suiteId: result.suite_id, suiteName: result.suite_name });
      await refreshRuns();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || '转为评测套件失败');
    } finally {
      setMaterializingId(null);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles size={16} />
          <h3 style={{ margin: 0 }}>合成评测用例</h3>
          <span className="text-muted text-sm">LLM 模拟用户生成带轨迹的评测用例，可直接转为评测套件绑定目标验证 / 自我进化</span>
        </div>
        <button onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-2">
          {error && (
            <div className="card mb-2" style={{ borderColor: 'var(--color-error, #dc2626)' }}>
              <p style={{ margin: 0, color: 'var(--color-error, #dc2626)' }}>{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-2 mb-2">
            <div className="flex gap-2 items-center">
              <label style={{ width: 100, fontSize: 13 }} className="text-muted">用例数量</label>
              <input
                type="number"
                min="1"
                max="10"
                value={numCases}
                onChange={(e) => setNumCases(e.target.value)}
                style={{ width: 120 }}
              />
              <label style={{ width: 80, fontSize: 13 }} className="text-muted">对话轮数</label>
              <input
                type="number"
                min="2"
                max="12"
                value={maxTurns}
                onChange={(e) => setMaxTurns(e.target.value)}
                style={{ width: 120 }}
              />
            </div>
            <div className="flex gap-2 items-center">
              <label style={{ width: 100, fontSize: 13 }} className="text-muted">关注点</label>
              <input
                type="text"
                style={{ flex: 1 }}
                value={focus}
                placeholder="如：退货流程、越界敏感问题（留空覆盖多样化场景）"
                onChange={(e) => setFocus(e.target.value)}
              />
              <button className="primary" disabled={creating} onClick={handleCreate}>
                <Plus size={14} /> {creating ? '合成中…' : '开始合成'}
              </button>
            </div>
          </div>

          {progress && (
            <p className="text-muted text-sm mb-2">
              <RotateCw size={12} style={{ verticalAlign: -2 }} /> {progress}
            </p>
          )}

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : runs.length === 0 ? (
            <div className="empty" style={{ padding: '16px 0' }}>
              <FlaskConical size={24} />
              <p className="text-muted text-sm">还没有合成批次，点击「开始合成」生成第一批评测用例</p>
            </div>
          ) : (
            <div className="list">
              {runs.map((run) => {
                const meta = STATUS_META[run.status] || STATUS_META.pending;
                const expandedCase = expandedRunId === run.id;
                const materializedHere = materialized?.runId === run.id;
                return (
                  <div key={run.id} className="list-item" style={{ display: 'block' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setExpandedRunId(expandedCase ? null : run.id)}>
                        <span className={`badge ${meta.badge}`}>{meta.label}</span>
                        <strong style={{ fontSize: 13 }}>
                          {run.params.num_cases} 个用例 · 最多 {run.params.max_turns} 轮
                          {run.params.focus ? ` · 关注：${run.params.focus}` : ''}
                        </strong>
                        <span className="text-muted text-sm">
                          {run.created_at ? new Date(run.created_at).toLocaleString() : ''}
                        </span>
                        {expandedCase ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>
                      <div className="flex gap-2">
                        {run.status === 'failed' && run.error && (
                          <span className="text-muted text-sm" style={{ color: 'var(--color-error, #dc2626)' }}>
                            {run.error.slice(0, 60)}
                          </span>
                        )}
                        {run.status === 'succeeded' && !materializedHere && (
                          <button
                            disabled={materializingId === run.id}
                            onClick={() => handleMaterialize(run)}
                            style={{ padding: '4px 10px' }}
                          >
                            {materializingId === run.id ? '转换中…' : '转为评测套件'}
                          </button>
                        )}
                        {materializedHere && (
                          <button
                            className="primary"
                            style={{ padding: '4px 10px' }}
                            onClick={() => onMaterialized?.(materialized.suiteId)}
                          >
                            创建目标验证
                          </button>
                        )}
                      </div>
                    </div>

                    {materializedHere && (
                      <p className="text-muted text-sm mt-1">
                        已转为套件 <code>{materialized.suiteId}</code>（{materialized.suiteName}），
                        点击「创建目标验证」绑定该套件跑分。
                      </p>
                    )}

                    {expandedCase && (
                      <div className="mt-2">
                        {run.cases.length === 0 ? (
                          <p className="text-muted text-sm">暂无用例</p>
                        ) : (
                          run.cases.map((c) => (
                            <div key={c.case_id} className="card" style={{ marginBottom: 8, padding: '8px 12px' }}>
                              <div className="flex items-center gap-2">
                                <strong style={{ fontSize: 13 }}>{c.title}</strong>
                                <span className={`badge ${c.case_type === 'boundary' ? 'warning' : c.case_type === 'tool_call' ? 'info' : 'success'}`}>
                                  {CASE_TYPE_LABEL[c.case_type] || c.case_type}
                                </span>
                              </div>
                              <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                                <strong>开场白：</strong>{c.starting_prompt}
                              </p>
                              {c.expected.rubric && (
                                <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                                  <strong>评分细则（仅判分层使用）：</strong>{c.expected.rubric}
                                </p>
                              )}
                              <details style={{ marginTop: 6 }}>
                                <summary className="text-muted text-sm" style={{ cursor: 'pointer' }}>对话轨迹（{c.trace.length} 轮）</summary>
                                <div className="mt-1" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                  {c.trace.map((t, i) => (
                                    <p key={i} className="text-sm" style={{ margin: '2px 0' }}>
                                      <strong className={t.role === 'user' ? undefined : 'text-muted'}>
                                        {t.role === 'user' ? '用户' : '助手'}：
                                      </strong>
                                      {t.content}
                                    </p>
                                  ))}
                                </div>
                              </details>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
